import React, { Fragment, useEffect, useRef } from 'react'
import { Cell, Order, UnavailableWindow } from '@m4a3/api-client';
import { getShortIdForOrder } from '../../utils/orderUtils';
import { LandingPad } from '../../models/landing-pad.interface';
import { convertUtcTimeStringForTimezone, isoStringToDateTimeObject } from '../../utils/date-utils';
import TimeGrid from './TimeGrid';
import { inactiveStatuses } from '../../constants';
import { DateTime } from 'luxon';

interface CalendarComponentProps {
  readonly onSelectOrder: (order: Order) => void;
  readonly onSelectEditUnavailableBlockType: (block: UnavailableWindow) => void;
  readonly orders: Order[];
  readonly timetablePads: LandingPad[];
  readonly dronesWithoutAssignedPad: string[];
  readonly unavailableWindows: UnavailableWindow[];
  readonly cell: Cell;
  readonly earliestOpeningTime: string;
  readonly latestClosingTime: string;
}

const numHoursShown = 24;
const numRowsPerHour = 12;
const totalRows = numHoursShown * numRowsPerHour;
const numRowsForOrder = 2;

export default function CalendarComponent(props: CalendarComponentProps) {
  const container = useRef(null)
  const containerOffset = useRef(null)
  const containerNav = useRef(null)
  const startOfDayInCellAsIso: string = DateTime.now().setZone(props.cell?.timezone).startOf('day').toJSDate().toISOString();
  const endOfDayInCellAsIso: string = DateTime.now().setZone(props.cell?.timezone).endOf('day').toJSDate().toISOString();

  const getTotalColumns = () => {
    // Always have "No Pad" column (index 0) + actual pad columns
    const padColumns = props.timetablePads?.length || 0;
    const noPadColumn = 1; // Always present at index 0
    return noPadColumn + padColumns;
  };

  useEffect(() => {
    // Set the container scroll position based on the current time.
    container.current.scrollTop = getPixelDistanceToCurrentTime();
  }, []);

  const getRowNumberForTime = (isoStartDateString: string): number => {
    const startOfBlockAsDate = isoStringToDateTimeObject(isoStartDateString, props.cell?.timezone).toJSDate();
    const totalSecondsInDay = numHoursShown * 60 * 60;
    const paddingRows = 2;
    const secondsPassed = ((startOfBlockAsDate.getHours()) * 60 * 60) +
                          (startOfBlockAsDate.getMinutes() * 60) +
                          startOfBlockAsDate.getSeconds();

    return Math.round((secondsPassed / totalSecondsInDay) * totalRows) + paddingRows;
  };

  const getSpanForRange = (isoStartDateString: string, isoEndDateString: string): number => {

    const startDate = isoStringToDateTimeObject(isoStartDateString, props.cell?.timezone).toJSDate();
    const endDate = isoStringToDateTimeObject(isoEndDateString, props.cell?.timezone).toJSDate();
    const totalSecondsInDay = numHoursShown * 60 * 60;
    const diffMilliseconds = Math.abs(startDate.getTime() - endDate.getTime());
    const durationInSeconds = diffMilliseconds / 1000;

    return Math.round(durationInSeconds / totalSecondsInDay * totalRows);
  };

  const getPixelDistanceToCurrentTime = (): number => {
    if (!container.current) {
      return 0;
    }
    const totalMinutesInDay = numHoursShown * 60;
    const currentMinute = isoStringToDateTimeObject(new Date().toISOString(), props.cell?.timezone).toJSDate().getHours() * 60
    return ((container.current.scrollHeight - containerNav.current.offsetHeight - containerOffset.current.offsetHeight) * currentMinute) / totalMinutesInDay;
  }

  // returns relevant display info for a given order, this is used in other functions
  // to calculate if it overlaps with any other orders.
  const getOrderDisplayDetails = (orderId: string): {time: string; numSpans: number; aircraft: string | null } => {
    const order = getTodaysOrders()?.find(({ id }: Order) => id === orderId);
    return {
      time: order?.expectedDeliveryDate,
      numSpans: numRowsForOrder,
      aircraft: order?.aircraft,
    };
  };

  // Checks if there is any overlap between two items on a CSS grid.
  // item 1 spans from startRow1 to endRow1, and item 2 spans startRow2 to endRow2.
  const isOverlapping = (startRow1: number, endRow1: number, startRow2: number, endRow2: number): boolean => {
    const orderStartsWithinAnotherOrderRow = startRow1 >= startRow2 && startRow1 <= endRow2;
    const orderEndsWithinAnotherOrderRow = endRow1 >= startRow2 && endRow1 <= endRow2;
    const orderIsEqualToOrLargerThanAnotherOrderRow = startRow1 <= startRow2 && endRow1 >= endRow2;

    return orderStartsWithinAnotherOrderRow || orderEndsWithinAnotherOrderRow || orderIsEqualToOrLargerThanAnotherOrderRow;
  };


  // returns a list of orders which overlap with the order we're checking
  // it is sorted by expectedDeliveryDate
  const getOverlappingOrders = (orderId: string, startRow: number, endRow: number): Order[] => {
    return getTodaysOrders()?.filter((order: Order) => {
      const startRowForOrder = getRowNumberForTime(order.expectedDeliveryDate);
      const endRowForOrder = startRowForOrder + numRowsForOrder - 1;

      return isOverlapping(startRow, endRow, startRowForOrder, endRowForOrder) && order.aircraft === getOrderDisplayDetails(orderId).aircraft;
    })
    .sort((orderA, orderB) => orderA.id.localeCompare(orderB.id))
    .sort((orderA, orderB) => new Date(orderA.expectedDeliveryDate).getTime() - new Date(orderB.expectedDeliveryDate).getTime());
  };

  // returns the appropriate CSS properties for a given order to ensure it doesn't
  // block any other orders in the list. If no overlapping orders it returns no CSS properties.
  const handleOverlappingOrders = (orderId: string): React.CSSProperties => {
    const { time, numSpans } = getOrderDisplayDetails(orderId);

    const startRow = getRowNumberForTime(time);
    const endRow = startRow + numSpans - 1;

    const overlappingOrders = getOverlappingOrders(orderId, startRow, endRow);

    if (overlappingOrders.length > 0) {
      const indexWhereOrderBelongs = overlappingOrders.findIndex(({ id }: Order) => id == orderId);

      return {
        width: `${100 / overlappingOrders.length}%`,
        left: `${indexWhereOrderBelongs * (100 / overlappingOrders.length)}%`,
      };
    }
    return {};
  };

  const getTodaysOrders = (): Order[] | undefined => {
    return props.orders?.filter((order: Order) => {
      const orderExpectedForDay = new Date(order.expectedDeliveryDate).getDate();
      const today = new Date().getDate();
      return orderExpectedForDay === today;
    })
  }

  return (<div style={{height: '100%', position: 'relative', width: '100%'}}>
    <div style={{
      width: '100%',
      height: '100%',
      overflow: 'auto',
      position: 'absolute',
      paddingBottom: '50px'
    }} className="flex h-full flex-col">
      <div ref={container} className="isolate flex flex-auto flex-col overflow-auto bg-white">
        <div style={{ width: '165%' }} className="flex max-w-full flex-none flex-col sm:max-w-none md:max-w-full">
          <div
            ref={containerNav}
            className="sticky top-0 z-30 flex-none bg-white shadow ring-1 ring-black ring-opacity-5 sm:pr-8"
          >
            <div 
              className="-mr-px hidden divide-x divide-gray-100 border-r border-gray-100 text-sm leading-6 text-gray-500 sm:grid"
              style={{ gridTemplateColumns: `repeat(${getTotalColumns()}, 1fr)` }}
            >
              <div className="col-end-1 w-14" />
              
              {/* Always show "No Pad" column first */}
              <div key="no-pad-column" className="flex items-center justify-center py-3">
                <span className="font-semibold text-gray-900 text-sm">
                  No Pad
                </span>
              </div>
              
              {/* Show actual pads */}
              {props.timetablePads && props.timetablePads.length > 0 && 
                props.timetablePads.map(({name, aircraft}) => {
                  return (<div key={`pad-name-${name}`} className="flex flex-col items-center justify-center py-3 gap-1">
                    <span className="font-semibold text-gray-900 text-sm">
                      {name}
                    </span>
                    <span className="font-semibold text-gray-900">
                      ({aircraft})
                    </span>
                  </div>);
                })
              }
            </div>
          </div>
          <div className="flex flex-auto">
            <div className="sticky left-0 z-10 w-14 flex-none bg-white ring-1 ring-gray-100" />
            <div className="grid flex-auto grid-cols-1 grid-rows-1">
              {/* Horizontal lines */}
              <div
                className="col-start-1 col-end-2 row-start-1 grid divide-y divide-gray-100"
                style={{ gridTemplateRows: `repeat(${numHoursShown * 2}, minmax(4rem, 1fr))` }}
              >
                <div ref={containerOffset} className="row-end-1 h-7"></div>
                <TimeGrid />
              </div>
              {/* Vertical lines */}
              <div 
                className="col-start-1 col-end-2 row-start-1 hidden grid-rows-1 divide-x divide-gray-100 sm:grid"
                style={{ gridTemplateColumns: `repeat(${getTotalColumns()}, 1fr)` }}
              >
                {/* Always show "No Pad" column first */}
                <div className="col-start-1 row-span-full" key="column-no-pad" />
                
                {/* Show actual pad columns */}
                {props.timetablePads && props.timetablePads.length > 0 && 
                  props.timetablePads.map((d, i) => <div className={`col-start-${i+2} row-span-full`} key={`column-pad-${d.name}`} />)
                }
                
                <div className={`col-start-${getTotalColumns() + 1} row-span-full w-8`} />
              </div>
              <ol
                className="col-start-1 col-end-2 row-start-1 grid grid-cols-1 sm:pr-8"
                style={{ 
                  gridTemplateRows: `2rem repeat(${totalRows}, minmax(0, 1fr)) auto`,
                  gridTemplateColumns: `repeat(${getTotalColumns()}, 1fr)`
                }}
              >
                {/* Timeline elements for pads */}
                {props.timetablePads && props.timetablePads.length > 0 ? (
                  props.timetablePads.map(((drone, index) => {
                    // Add 2 to index: +1 for "No Pad" column at index 0, +1 for 1-based CSS grid
                    const columnIndex = index + 2;
                    return <Fragment key={`opening-time-line-for-drone-${drone.name}`}>
                    <li className={`relative mt-px hidden sm:col-start-${columnIndex} sm:flex opacity-70 bg-gray-100`} style={{ gridRow: `${getRowNumberForTime(startOfDayInCellAsIso)} / span ${getSpanForRange(startOfDayInCellAsIso, props.earliestOpeningTime)}` }}></li>
                    <li className={`relative mt-px hidden sm:col-start-${columnIndex} sm:flex opacity-70 bg-gray-100`} style={{ gridRow: `${getRowNumberForTime(props.latestClosingTime)} / span ${getSpanForRange(props.latestClosingTime, endOfDayInCellAsIso)}` }}></li>
                    <li className={`relative mt-px hidden sm:col-start-${columnIndex} sm:flex border-dashed border-t-2 border-blue-500`} style={{ gridRow: `${getRowNumberForTime(new Date().toISOString())} / span 1` }}></li>
                    {props.unavailableWindows?.filter(unavailWindow => unavailWindow.limitToUavId === null || unavailWindow.limitToUavId === drone.aircraft).map(unavailWindow => {
                      return (<li
                        key={`${unavailWindow.id}-${drone.name}`}
                        className={`relative mt-px hidden sm:col-start-${columnIndex} sm:flex opacity-70`}
                        style={{
                          gridRow: `${getRowNumberForTime(unavailWindow.startDate)} / span ${getSpanForRange(unavailWindow.startDate, unavailWindow.endDate)}`,
                        }}
                        onClick={() => props.onSelectEditUnavailableBlockType(unavailWindow)}>
                        <button
                          type='button'
                          className="group absolute inset-1 flex flex-col overflow-y-auto rounded-lg bg-red-100 p-2 text-xs leading-5 hover:bg-red-200"
                        >
                          <p className="order-1 font-semibold text-red-700">{unavailWindow.reason}</p>
                          <p className="text-red-500 group-hover:text-red-700">
                            <time dateTime={unavailWindow.startDate}>{convertUtcTimeStringForTimezone(unavailWindow.startDate, props.cell?.timezone)}</time>
                            {' - '}
                            <time dateTime={unavailWindow.endDate}>{convertUtcTimeStringForTimezone(unavailWindow.endDate, props.cell?.timezone)}</time>
                          </p>
                        </button>
                      </li>);
                    })}
                    </Fragment>
                  }))
                ) : null}

                {getTodaysOrders().map((order: Order) => {
                  let indexToAddTo = -1;
                  
                  // Match order to pad using originLandingPadId
                  if (order.originLandingPadId) {
                    
                    // Find pad index by matching originLandingPadId to pad.id
                    const padIndex = props.timetablePads?.findIndex((pad) => pad.id === order.originLandingPadId);
                    
                    if (padIndex !== undefined && padIndex >= 0) {
                      // Add 1 because "No Pad" column is at index 0, so actual pads start at index 1
                      indexToAddTo = padIndex + 1;
                    }
                  } else {
                    // No originLandingPadId: place in "No Pad" column (index 0)
                    indexToAddTo = 0;
                  }

                  const isOrderFulfilled = inactiveStatuses.includes(order.status)
                  const doesOrderNeedMCAttention = (): boolean => {
                    const isReadyForDeliver = [
                      'RECEIVED_FROM_STORE',
                      'READY',
                      'DISPATCHED'
                    ].includes(order.status);
                    const expectedDeliveryInTimezone = isoStringToDateTimeObject(order.expectedDeliveryDate, props.cell?.timezone);
                    const nowInTimezone = isoStringToDateTimeObject(new Date().toISOString(), props.cell?.timezone);
                    const timeDifference = expectedDeliveryInTimezone.diff(nowInTimezone, 'minutes').toObject().minutes;
                    const opsTimeDurationMinutes = 5;

                    return isReadyForDeliver || ((timeDifference > 0 && timeDifference < opsTimeDurationMinutes));
                  };

                  const isOrderLate = (): boolean => {
                    const expectedDeliveryInTimezone = isoStringToDateTimeObject(order.expectedDeliveryDate, props.cell?.timezone);
                    const nowInTimezone = isoStringToDateTimeObject(new Date().toISOString(), props.cell?.timezone);
                    return nowInTimezone.diff(expectedDeliveryInTimezone).milliseconds > 0;
                  }

                  const getColorForOrder = ():string => {
                    if (isOrderFulfilled) {
                      return 'gray';
                    }

                    if (isOrderLate()) {
                      return 'red';
                    }

                    if (doesOrderNeedMCAttention()) {
                      return 'yellow'
                    }
                    return 'blue';
                  };

                  return (<li
                    title={`#${getShortIdForOrder(order)} - ETA: ${convertUtcTimeStringForTimezone(order.expectedDeliveryDate, props.cell?.timezone)}`}
                    className={`flex-1 no-scrollbar relative mt-px hidden sm:col-start-${indexToAddTo + 1} sm:flex opacity-90`}
                    style={{
                      gridRow: `${getRowNumberForTime(order.expectedDeliveryDate)} / span ${numRowsForOrder}`,
                      ...handleOverlappingOrders(order.id)
                    }}
                    key={order.id}
                    onClick={() => props.onSelectOrder(order)} >
                    <div className={`no-scrollbar group absolute inset-0 flex flex-col overflow-y-auto rounded-lg bg-${getColorForOrder()}-100 hover:bg-${getColorForOrder()}-200 ${isOrderFulfilled ? 'line-through' : ''}} px-1 text-xs leading-5`}>
                      <p className={`order-1 text-${getColorForOrder()}-700`}>
                        <span className='font-semibold'>#{getShortIdForOrder(order)}</span> - ETA: <time dateTime={order.expectedDeliveryDate}>{convertUtcTimeStringForTimezone(order.expectedDeliveryDate, props.cell?.timezone)}</time>
                      </p>
                    </div>
                  </li>);
                })}
              </ol>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>)
}
